import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, ArrowRight, CreditCard, FileText, QrCode, Download, 
  Smartphone, Play, Pause, RotateCcw, X,
  Heart, AlertTriangle, Phone, Pill, CheckCircle
} from 'lucide-react';

const WalkthroughSlideshow = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showSlideshow, setShowSlideshow] = useState(true);

  const slides = [
    {
      id: 1,
      title: "Step 1: Open the App",
      subtitle: "Tap the big button to get started",
      icon: Smartphone,
      color: "bg-[#E63333]",
      visual: (
        <div className="bg-gradient-to-b from-[#D92B2B] to-[#C02424] rounded-2xl p-6 shadow-2xl max-w-[200px] mx-auto">
          <div className="bg-white/20 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
            <QrCode className="h-6 w-6 text-white" />
          </div>
          <div className="text-white text-center text-sm font-bold mb-3">My EmergencyCard</div>
          <div className="bg-white rounded-lg py-2 px-4 text-[#D92B2B] text-xs font-bold text-center animate-pulse">
            Create Your Card - $1
          </div>
        </div>
      ),
      instruction: "👆 Tap this button"
    },
    {
      id: 2,
      title: "Step 2: Quick $1 Payment",
      subtitle: "One-time only - no subscription!",
      icon: CreditCard,
      color: "bg-green-500",
      visual: (
        <div className="bg-white rounded-2xl p-4 shadow-2xl max-w-[200px] mx-auto">
          <div className="text-center mb-3">
            <div className="text-green-600 font-bold text-sm">Secure Checkout</div>
            <div className="text-2xl font-bold text-slate-800">$1.00</div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-2 text-xs">
              <div className="w-8 h-5 bg-gradient-to-r from-blue-600 to-blue-400 rounded"></div>
              <span className="text-slate-600">Credit Card</span>
            </div>
            <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-2 text-xs">
              <div className="w-8 h-5 bg-black rounded flex items-center justify-center text-white text-[8px]">Pay</div>
              <span className="text-slate-600">Apple Pay</span>
            </div>
          </div>
          <div className="bg-green-500 text-white text-xs rounded-lg py-2 text-center mt-3 font-bold animate-pulse">
            Pay Now
          </div>
        </div>
      ),
      instruction: "💳 Use Card, Apple Pay, or Google Pay"
    },
    {
      id: 3,
      title: "Step 3: Enter Your Info",
      subtitle: "Fill in your medical details",
      icon: FileText,
      color: "bg-blue-500",
      visual: (
        <div className="bg-white rounded-2xl p-4 shadow-2xl max-w-[200px] mx-auto text-left">
          <div className="space-y-2">
            <div>
              <div className="text-[10px] text-slate-500 mb-1">Full Name</div>
              <div className="bg-slate-100 rounded px-2 py-1 text-xs">John Smith</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500 mb-1">Blood Group</div>
              <div className="bg-[#FEE2E2] text-[#C02424] rounded px-2 py-1 text-xs font-bold">O+</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500 mb-1">Allergies</div>
              <div className="flex gap-1">
                <span className="bg-[#E63333] text-white rounded px-1.5 py-0.5 text-[9px]">Penicillin</span>
                <span className="bg-amber-500 text-white rounded px-1.5 py-0.5 text-[9px]">Peanuts</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500 mb-1">Medications</div>
              <div className="flex gap-1">
                <span className="bg-blue-100 text-blue-700 rounded px-1.5 py-0.5 text-[9px]">Metformin</span>
              </div>
            </div>
          </div>
        </div>
      ),
      instruction: "📝 Add allergies, medications, contacts"
    },
    {
      id: 4,
      title: "Step 4: Create Your Card",
      subtitle: "Tap the green button",
      icon: QrCode,
      color: "bg-purple-500",
      visual: (
        <div className="bg-white rounded-2xl p-4 shadow-2xl max-w-[200px] mx-auto">
          <div className="border-2 border-dashed border-slate-300 rounded-xl p-4 mb-3">
            <div className="grid grid-cols-5 gap-1">
              {[...Array(25)].map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-sm ${Math.random() > 0.5 ? 'bg-slate-800' : 'bg-white'}`}></div>
              ))}
            </div>
          </div>
          <div className="bg-green-500 text-white text-xs rounded-lg py-2 text-center font-bold animate-pulse">
            ✓ Create Emergency Card
          </div>
        </div>
      ),
      instruction: "👆 Your QR code appears instantly!"
    },
    {
      id: 5,
      title: "Step 5: Save Your QR Code!",
      subtitle: "Important: We don't store your data",
      icon: Download,
      color: "bg-amber-500",
      visual: (
        <div className="bg-white rounded-2xl p-4 shadow-2xl max-w-[200px] mx-auto">
          <div className="bg-slate-100 rounded-xl p-3 mb-3">
            <div className="grid grid-cols-7 gap-0.5 mx-auto w-fit">
              {[...Array(49)].map((_, i) => (
                <div key={i} className={`w-2 h-2 ${Math.random() > 0.4 ? 'bg-slate-800' : 'bg-white'}`}></div>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <div className="bg-blue-500 text-white text-[10px] rounded-lg py-1.5 text-center font-bold flex items-center justify-center gap-1">
              <Download className="h-3 w-3" /> Save to Phone
            </div>
            <div className="bg-green-500 text-white text-[10px] rounded-lg py-1.5 text-center font-bold">
              Share QR Code
            </div>
          </div>
        </div>
      ),
      instruction: "📱 Save as wallpaper or print for wallet!"
    },
    {
      id: 6,
      title: "Done! When Scanned...",
      subtitle: "Anyone can see your info instantly",
      icon: CheckCircle,
      color: "bg-green-600",
      visual: (
        <div className="bg-gradient-to-b from-[#D92B2B] to-[#C02424] rounded-2xl p-3 shadow-2xl max-w-[200px] mx-auto">
          <div className="bg-white rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-[#E63333]" />
              <span className="text-xs font-bold text-slate-800">EMERGENCY CARD</span>
            </div>
            <div className="text-sm font-bold text-slate-800">John Smith</div>
            <div className="text-xs text-slate-600">45 years • O+ Blood</div>
            <div className="flex gap-1 mt-2">
              <span className="bg-[#E63333] text-white rounded px-1 py-0.5 text-[8px]">⚠️ Penicillin</span>
            </div>
            <div className="bg-green-500 text-white text-[10px] rounded py-1 text-center mt-2 flex items-center justify-center gap-1">
              <Phone className="h-3 w-3" /> Call Jane (Wife)
            </div>
          </div>
        </div>
      ),
      instruction: "🏥 First responders see everything they need!"
    }
  ];

  // Auto-advance slides
  useEffect(() => {
    if (!isPlaying) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000); // 6 seconds per slide - slower for older users

    return () => clearInterval(timer);
  }, [isPlaying, slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsPlaying(false);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const restart = () => {
    setCurrentSlide(0);
    setIsPlaying(true);
  };

  const currentSlideData = slides[currentSlide];
  const Icon = currentSlideData.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <button onClick={() => navigate(-1)} className="text-white p-2">
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="text-white text-center">
          <div className="text-sm font-medium">How It Works</div>
          <div className="text-xs text-slate-400">{currentSlide + 1} of {slides.length}</div>
        </div>
        <button onClick={() => navigate('/')} className="text-white p-2">
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="px-4 mb-4">
        <div className="flex gap-1">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? currentSlideData.color 
                  : index < currentSlide 
                    ? 'bg-white/60' 
                    : 'bg-white/20'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Slide Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {/* Icon */}
        <div className={`${currentSlideData.color} p-4 rounded-full mb-6 shadow-lg`}>
          <Icon className="h-8 w-8 text-white" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-white text-center mb-2">
          {currentSlideData.title}
        </h2>
        <p className="text-slate-400 text-center mb-8">
          {currentSlideData.subtitle}
        </p>

        {/* Visual */}
        <div className="transform transition-all duration-500 hover:scale-105">
          {currentSlideData.visual}
        </div>

        {/* Instruction */}
        <div className="mt-6 bg-white/10 backdrop-blur rounded-full px-4 py-2">
          <p className="text-white text-sm font-medium">
            {currentSlideData.instruction}
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="p-6">
        {/* Playback Controls */}
        <div className="flex items-center justify-center gap-4 mb-4">
          <button
            onClick={prevSlide}
            className="p-3 bg-white/10 rounded-full text-white hover:bg-white/20"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className={`p-4 rounded-full text-white ${isPlaying ? 'bg-[#E63333]' : 'bg-green-500'}`}
          >
            {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
          </button>
          
          <button
            onClick={nextSlide}
            className="p-3 bg-white/10 rounded-full text-white hover:bg-white/20"
          >
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>

        {/* Restart */}
        {currentSlide === slides.length - 1 && (
          <div className="flex gap-3">
            <Button
              onClick={restart}
              variant="outline"
              className="flex-1 h-12 bg-transparent border-white/30 text-white hover:bg-white/10"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Watch Again
            </Button>
            <Button
              onClick={() => navigate('/create')}
              className="flex-1 h-12 bg-white text-[#D92B2B] hover:bg-[#FEF2F2] font-bold"
            >
              Create My Card
            </Button>
          </div>
        )}

        {currentSlide < slides.length - 1 && (
          <Button
            onClick={() => navigate('/create')}
            className="w-full h-12 bg-white/10 text-white hover:bg-white/20 border border-white/20"
          >
            Skip & Create Card
          </Button>
        )}
      </div>
    </div>
  );
};

export default WalkthroughSlideshow;
